import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { MapPin, Star, ShieldCheck, CheckCircle, CaretRight, Question } from "@phosphor-icons/react";

export default function LocalPage() {
  const { localSlug } = useParams();
  const [page, setPage] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound

  useEffect(() => {
    let alive = true;
    setStatus("loading");
    api.get(`/local/${encodeURIComponent(localSlug)}`)
      .then(({ data }) => { if (alive) { setPage(data); setStatus("ok"); } })
      .catch(() => { if (alive) setStatus("notfound"); });
    return () => { alive = false; };
  }, [localSlug]);

  // Manage <head> tags (title, description, canonical, robots, JSON-LD) per page
  useEffect(() => {
    if (!page) return;
    document.title = page.meta.title;
    upsertMeta("description", page.meta.description);
    upsertLink("canonical", page.meta.canonical);
    upsertMeta("og:title", page.meta.title, "property");
    upsertMeta("og:description", page.meta.description, "property");
    upsertMeta("og:url", page.meta.canonical, "property");
    upsertMeta("og:type", "website", "property");

    // JSON-LD — Service + FAQPage schema for rich results
    const ld = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          serviceType: page.trade.name,
          areaServed: { "@type": "City", name: page.city.name },
          provider: {
            "@type": "Organization",
            name: "MyHandyHub",
            url: "https://myhandyhub.co.uk",
          },
          name: page.hero.headline,
          description: page.meta.description,
        },
        {
          "@type": "FAQPage",
          mainEntity: page.faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    };
    upsertLd("local-page-ld", ld);

    return () => {
      // Reset when leaving page — App.js will reset title elsewhere
      document.getElementById("local-page-ld")?.remove();
    };
  }, [page]);

  if (status === "loading") {
    return <Layout><div className="max-w-3xl mx-auto px-5 py-16 text-center text-[var(--text-muted)]">Loading…</div></Layout>;
  }
  if (status === "notfound") {
    return <Navigate to="/" replace />;
  }
  if (!page) return null;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-10 sm:py-14" data-testid="local-page">
        {/* Hero */}
        <div>
          <div className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--brand-orange)" }}>
            <MapPin size={12} weight="fill" className="inline -mt-0.5 mr-1" />
            {page.city.region}
          </div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.05]" style={{ color: "var(--brand-green)" }}>
            {page.hero.headline}
          </h1>
          <p className="mt-4 text-lg sm:text-xl text-[var(--text-muted)] max-w-2xl">
            {page.hero.sub}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/register?role=customer" className="btn-primary" data-testid="local-post-job">
              Post a job — free
              <CaretRight size={16} weight="bold" />
            </Link>
            <Link to={`/find-trades?trade=${page.trade.slug}`} className="btn-ghost">
              Browse local {page.trade.plural.toLowerCase()}
            </Link>
          </div>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[var(--text-muted)]">
            <span className="inline-flex items-center gap-1.5"><CheckCircle size={16} weight="fill" style={{ color: "var(--brand-green)" }} /> Free for customers</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={16} weight="fill" style={{ color: "var(--brand-green)" }} /> Verified traders</span>
            <span className="inline-flex items-center gap-1.5"><Star size={16} weight="fill" style={{ color: "var(--brand-orange)" }} /> Real reviews</span>
          </div>
        </div>

        {/* Local trades card list */}
        <section className="mt-14">
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl" style={{ color: "var(--brand-green)" }}>
            {page.matches.length > 0
              ? `${page.trade.plural} available in ${page.city.name} right now`
              : `Trades for ${page.city.name} coming soon`}
          </h2>
          {page.matches.length > 0 ? (
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              {page.matches.map((m) => (
                <Link
                  key={m.user_id}
                  to={`/trades/${m.user_id}`}
                  className="brand-card p-5 flex items-start gap-4 hover:shadow-md transition-shadow"
                  data-testid={`local-match-${m.user_id}`}
                >
                  <div className="w-14 h-14 rounded-full bg-[#FAF8F2] flex items-center justify-center text-lg font-extrabold flex-shrink-0" style={{ color: "var(--brand-green)" }}>
                    {m.profile_photo ? (
                      <img src={m.profile_photo} alt={m.business_name || m.name} className="w-full h-full rounded-full object-cover" />
                    ) : (m.business_name || m.name || "?").slice(0, 1)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-display font-extrabold text-base" style={{ color: "var(--brand-green)" }}>{m.business_name || m.name}</span>
                      {m.verified && <ShieldCheck size={16} weight="fill" style={{ color: "var(--brand-green)" }} title="Verified" />}
                    </div>
                    <div className="text-xs text-[var(--text-muted)] mt-0.5">{m.postcode}</div>
                    {m.avg_rating > 0 && (
                      <div className="mt-1.5 inline-flex items-center gap-1 text-sm">
                        <Star size={14} weight="fill" style={{ color: "var(--brand-orange)" }} />
                        <strong style={{ color: "var(--brand-green)" }}>{m.avg_rating.toFixed(1)}</strong>
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="brand-card p-6 sm:p-8 mt-5 text-center" style={{ background: "linear-gradient(135deg, rgba(17,82,59,0.03), rgba(226,109,41,0.03))" }}>
              <p className="text-base leading-relaxed text-[var(--text-muted)] max-w-xl mx-auto">
                We're actively signing up {page.trade.plural.toLowerCase()} in {page.city.name} right now.
                Join the waitlist as a customer and we'll email you the moment there's a great {page.trade.name.toLowerCase()} in your area — plus you qualify for a <strong style={{ color: "var(--brand-orange)" }}>£20 Amazon voucher</strong> as a thank-you.
              </p>
              <Link to="/" className="btn-primary mt-5" data-testid="local-join-waitlist">Join the waitlist</Link>
            </div>
          )}
        </section>

        {/* Common jobs */}
        <section className="mt-14">
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl" style={{ color: "var(--brand-green)" }}>
            Popular {page.trade.name.toLowerCase()} jobs in {page.city.name}
          </h2>
          <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {page.job_examples.map((ex, i) => (
              <div key={i} className="brand-card p-4 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "rgba(226,109,41,0.12)" }}>
                  <CheckCircle size={16} weight="fill" style={{ color: "var(--brand-orange)" }} />
                </span>
                <span className="text-sm">{ex}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Price hint */}
        <section className="mt-14 brand-card p-6" style={{ background: "#FAF8F2" }}>
          <h2 className="font-display font-extrabold text-xl" style={{ color: "var(--brand-green)" }}>
            How much does a {page.trade.name.toLowerCase()} cost in {page.city.name}?
          </h2>
          <p className="text-base mt-2 leading-relaxed text-[var(--text-main)]">
            <strong>Typically {page.price_hint}.</strong>
            {" "}Post your job on MyHandyHub to receive competing quotes from local {page.trade.plural.toLowerCase()} — you'll see what fair looks like before committing to anyone.
          </p>
        </section>

        {/* FAQ */}
        <section className="mt-14">
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl mb-5" style={{ color: "var(--brand-green)" }}>
            Common questions about hiring a {page.trade.name.toLowerCase()} in {page.city.name}
          </h2>
          <ul className="space-y-3">
            {page.faqs.map((f, i) => (
              <li key={i} className="brand-card p-5">
                <div className="flex items-start gap-3">
                  <Question size={22} weight="duotone" className="flex-shrink-0 mt-0.5" style={{ color: "var(--brand-orange)" }} />
                  <div>
                    <div className="font-display font-extrabold text-base" style={{ color: "var(--brand-green)" }}>{f.q}</div>
                    <p className="mt-1.5 text-sm leading-relaxed text-[var(--text-main)]">{f.a}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* Nearby links — internal linking boosts SEO */}
        {page.nearby.length > 0 && (
          <section className="mt-14">
            <h2 className="font-display font-extrabold text-lg" style={{ color: "var(--brand-green)" }}>
              Also serving nearby
            </h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {page.nearby.map((n) => (
                <span key={n} className="px-3 py-1.5 rounded-full text-xs font-bold" style={{ background: "#FAF8F2", color: "var(--brand-green)" }}>
                  {n}
                </span>
              ))}
            </div>
            <p className="text-xs text-[var(--text-muted)] mt-3">
              Postcode area: <strong>{page.postcode_prefix}</strong>
            </p>
          </section>
        )}

        {/* Bottom CTA */}
        <section className="mt-14 brand-card p-8 sm:p-10 text-center" style={{ background: "linear-gradient(135deg, rgba(17,82,59,0.06), rgba(226,109,41,0.05))" }}>
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl" style={{ color: "var(--brand-green)" }}>
            Ready to hire a {page.trade.name.toLowerCase()} in {page.city.name}?
          </h2>
          <p className="mt-2 text-base text-[var(--text-muted)] max-w-xl mx-auto">
            Post your job in 2 minutes. Get free quotes. Chat with local trades. Hire the one you like. All in-app.
          </p>
          <Link to="/register?role=customer" className="btn-primary mt-5" data-testid="local-bottom-cta">
            Post your job — free
            <CaretRight size={16} weight="bold" />
          </Link>
        </section>
      </div>
    </Layout>
  );
}

// ---------- head-tag helpers ----------

function upsertMeta(name, content, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertLd(id, obj) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
}
